import { useEffect, useState } from 'react';
import { VERDICTS, TONE_COLOR_VAR } from './verdicts';
import './ScorePanel.css';

export default function ScorePanel({ score, verdictKey }) {
  const verdict = VERDICTS[verdictKey] || VERDICTS.uncertain;
  const toneColor = TONE_COLOR_VAR[verdict.tone];

  const animatedScore = useCountUp(score, 1400);

  const radius = 130;
  const circumference = Math.PI * radius;
  const progress = (score / 100) * circumference;

  return (
    <div className="score-panel">
      <div className="panel-label">
        <span>CREDIBILITY INDEX</span>
        <span className="panel-label__sub">0—100 SCALE</span>
      </div>

      <div className="score-dial">
        <svg viewBox="0 0 320 180" className="score-dial__svg">
          {/* Tick marks */}
          {Array.from({ length: 21 }).map((_, i) => {
            const angle = Math.PI - (i / 20) * Math.PI;
            const isMajor = i % 5 === 0;
            const inner = isMajor ? 140 : 145;
            const outer = 152;
            const x1 = 160 + Math.cos(angle) * inner;
            const y1 = 160 - Math.sin(angle) * inner;
            const x2 = 160 + Math.cos(angle) * outer;
            const y2 = 160 - Math.sin(angle) * outer;
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={isMajor ? 'var(--fg-dim)' : 'var(--border)'}
                strokeWidth={isMajor ? 1 : 0.5}
              />
            );
          })}
          {/* Tick labels */}
          {[0, 25, 50, 75, 100].map((v) => {
            const angle = Math.PI - (v / 100) * Math.PI;
            const x = 160 + Math.cos(angle) * 122;
            const y = 160 - Math.sin(angle) * 122 + 4;
            return (
              <text
                key={v}
                x={x}
                y={y}
                fill="var(--fg-dim)"
                fontSize="9"
                fontFamily="var(--font-mono)"
                textAnchor="middle"
              >
                {v}
              </text>
            );
          })}
          {/* Track */}
          <path
            d="M 30 160 A 130 130 0 0 1 290 160"
            fill="none"
            stroke="var(--border)"
            strokeWidth="2"
          />
          {/* Progress */}
          <path
            d="M 30 160 A 130 130 0 0 1 290 160"
            fill="none"
            stroke={toneColor}
            strokeWidth="3"
            strokeDasharray={`${progress} ${circumference}`}
            className="score-dial__progress"
          />
          {/* Threshold markers at 45 and 75 */}
          {[45, 75].map((v) => {
            const angle = Math.PI - (v / 100) * Math.PI;
            const x = 160 + Math.cos(angle) * 130;
            const y = 160 - Math.sin(angle) * 130;
            return (
              <circle
                key={v}
                cx={x}
                cy={y}
                r="2.5"
                fill="var(--bg)"
                stroke="var(--fg-dim)"
                strokeWidth="1"
              />
            );
          })}
        </svg>

        <div className="score-dial__readout">
          <div className="score-dial__number" style={{ color: toneColor }}>
            {animatedScore}
          </div>
          <div className="score-dial__slash">/ 100</div>
        </div>
      </div>

      <div className="verdict-block">
        <div className="verdict-block__label">VERDICT</div>
        <div className="verdict-block__value">
          <span className="verdict-block__glyph" style={{ color: toneColor }}>
            {verdict.glyph}
          </span>
          <span>{verdict.label}</span>
        </div>
        <div className="threshold-legend">
          <LegendItem color="var(--accent-red)" range="< 45" label="manipulated" />
          <LegendItem color="var(--accent-amber)" range="45–74" label="uncertain" />
          <LegendItem color="var(--accent-green)" range="≥ 75" label="authentic" />
        </div>
      </div>
    </div>
  );
}

function LegendItem({ color, range, label }) {
  return (
    <div className="threshold-legend__item">
      <span className="threshold-legend__dot" style={{ color }}>
        ■
      </span>
      <span className="threshold-legend__range">{range}</span>
      <span className="threshold-legend__label">{label}</span>
    </div>
  );
}

function useCountUp(target, duration) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}
